import { Component, computed, DestroyRef, effect, ElementRef, inject, input, output, signal, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { KnowledgeBaseInterface } from '@vendasta/embeddings';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { RouteCommand } from '../interface/config.interface';
import { LoadingState } from '../interface/loading-state';
import { AiKnowledgeService } from '../services/ai-knowledge.service';

/**
 * Every assistant gets an implicit "primary" knowledge collection holding its directly-attached knowledge sources;
 * those ids are prefixed `KB-APP-AIASSISTANT`. They're managed by the knowledge-sources surface and must never be
 * offered as shared knowledge, so they're filtered out everywhere here — including other assistants' primaries.
 */
const ASSISTANT_PRIMARY_KB_ID_PREFIX = 'KB-APP-AIASSISTANT';

/**
 * Lists and connects shared knowledge collections to an app's embedding application. Unlike
 * {@link ApplicationKnowledgeComponent} (which manages individual knowledge *sources*), this manages the app's
 * `knowledge_base_keys` associations. ("Knowledge collection" is the UI term for an embeddings KnowledgeBase.)
 */
@Component({
  selector: 'ai-knowledge-application-knowledge-bases',
  imports: [
    TranslateModule,
    AiKnowledgeI18nModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    RouterModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './application-knowledge-bases.component.html',
  styleUrls: ['./application-knowledge-bases.component.scss'],
})
export class ApplicationKnowledgeBasesComponent {
  /** The embedding application id (e.g. `APP-AI<assistantId>`) whose knowledge-collection associations are managed. */
  readonly appId = input.required<string>();
  /** When false, connecting/disconnecting is disabled. */
  readonly enabled = input(true);

  readonly selectedKnowledgeBasesChange = output<{ knowledgeBases: KnowledgeBaseInterface[]; initialLoad?: boolean }>();
  readonly loadStatusChange = output<LoadingState>();

  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly allKnowledgeBases = signal<KnowledgeBaseInterface[]>([]);
  protected readonly selectedKnowledgeBases = signal<KnowledgeBaseInterface[]>([]);
  protected readonly searchValue = signal<string>('');
  protected readonly loadStatus = signal<LoadingState>('loading');

  protected readonly selectControl = new FormControl<string | KnowledgeBaseInterface>('');

  @ViewChild('knowledgeBaseInput') knowledgeBaseInput!: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild('trigger') matAutocompleteTrigger!: MatAutocompleteTrigger;

  /** Candidate KBs to connect: not the primary, not already selected, matching the search term. */
  protected readonly filteredKnowledgeBases = computed(() => {
    const selectedIds = new Set(this.selectedKnowledgeBases().map((kb) => kb.id));
    const search = this.searchValue();
    return this.allKnowledgeBases().filter(
      (kb) =>
        !this.isAssistantPrimaryKb(kb) &&
        !selectedIds.has(kb.id) &&
        (kb.name ?? '').toLowerCase().includes(search),
    );
  });

  constructor() {
    effect(() => {
      if (this.enabled()) {
        this.selectControl.enable({ emitEvent: false });
      } else {
        this.selectControl.disable({ emitEvent: false });
      }
    });

    this.selectControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (typeof value === 'string') {
        this.searchValue.set(value.toLowerCase());
      }
    });

    // Refetch whenever the app changes (the namespace tokens are resolved by the time appId is set).
    effect(() => {
      const appId = this.appId();
      if (appId) {
        void this.initialize();
      }
    });
  }

  async initialize(): Promise<void> {
    this.loadStatus.set('loading');
    this.loadStatusChange.emit('loading');
    try {
      const [connectedKeys, allKnowledgeBases] = await Promise.all([
        this.knowledgeService.listKnowledgeBaseKeysForApp(this.appId()),
        this.knowledgeService.listKnowledgeBases(false),
      ]);
      this.allKnowledgeBases.set(allKnowledgeBases);

      const connectedIds = new Set(connectedKeys.map((key) => key.knowledgeBaseId));
      const connected = allKnowledgeBases.filter((kb) => connectedIds.has(kb.id) && !this.isAssistantPrimaryKb(kb));

      this.selectedKnowledgeBases.set(connected);
      this.selectedKnowledgeBasesChange.emit({ knowledgeBases: connected, initialLoad: true });
      this.loadStatus.set('succeeded');
      this.loadStatusChange.emit('succeeded');
    } catch {
      this.loadStatus.set('failed');
      this.loadStatusChange.emit('failed');
    }
  }

  /** An assistant's primary knowledge collection (its private, source-backed KB) — never offered as shared knowledge. */
  protected isAssistantPrimaryKb(kb: KnowledgeBaseInterface): boolean {
    return !!kb.id && kb.id.startsWith(ASSISTANT_PRIMARY_KB_ID_PREFIX);
  }

  /** Route to the KB's detail page, or null when the host app didn't wire one — in which case the name renders as plain text. */
  protected detailRouteFor(kb: KnowledgeBaseInterface): RouteCommand[] | null {
    const id = kb.id;
    return id ? this.knowledgeService.buildKnowledgeBaseDetailRoute(id) : null;
  }

  protected addKnowledgeBase(kb: KnowledgeBaseInterface | undefined): void {
    if (!this.enabled() || !kb) {
      return;
    }
    if (this.selectedKnowledgeBases().some((selected) => selected.id === kb.id)) {
      this.clearInput();
      return;
    }
    const next = [...this.selectedKnowledgeBases(), kb];
    this.selectedKnowledgeBases.set(next);
    this.selectedKnowledgeBasesChange.emit({ knowledgeBases: next, initialLoad: false });
    this.clearInput();
    this.matAutocompleteTrigger?.closePanel();
  }

  protected removeKnowledgeBase(kb: KnowledgeBaseInterface): void {
    if (!this.enabled()) {
      return;
    }
    const next = this.selectedKnowledgeBases().filter((selected) => selected.id !== kb.id);
    this.selectedKnowledgeBases.set(next);
    this.selectedKnowledgeBasesChange.emit({ knowledgeBases: next, initialLoad: false });
  }

  protected chooseMatch(event: Event): void {
    event.preventDefault();
    if (this.filteredKnowledgeBases().length === 1) {
      this.addKnowledgeBase(this.filteredKnowledgeBases()[0]);
    }
  }

  private clearInput(): void {
    if (this.knowledgeBaseInput?.nativeElement?.value) {
      this.knowledgeBaseInput.nativeElement.value = '';
    }
    this.searchValue.set('');
    this.selectControl.setValue('');
  }
}
